import React, { useEffect, useState } from 'react';
import './ScreenerTable.css';
import screenerApi, { ScreenerStockResponse } from '../../services/screenerApi.ts';
import MiniChart from '../charts/MiniChart.tsx';
import MiniChartWithDrawings from '../charts/MiniChartWithDrawings.tsx';
import StockChartWithDrawings, { Drawing } from '../charts/StockChartWithDrawings.tsx';
import { isAIScreener as checkIsAI } from '../../config/screenerConfig.ts';

export interface ColumnConfig {
  key: keyof ScreenerStockResponse;
  label: string;
  visible: boolean;
}

export interface BackendScreenerTableProps {
  columns?: ColumnConfig[];
  viewChartButtonEnable?: boolean;
  pageSize?: number;
  data: ScreenerStockResponse[];
  loading: boolean;
  error?: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  screenerCode?: string; // if provided, "View Analysis" button shown when chartView === 'DRAWING'
}

const defaultColumns: ColumnConfig[] = [
  { key: 'stockName', label: 'Stock Name', visible: true },
  { key: 'symbol', label: 'Symbol', visible: true },
  { key: 'exchange', label: 'Exch', visible: true },
  { key: 'timeframe', label: 'Timeframe', visible: true },
  { key: 'capturedPrice', label: 'Captured Price', visible: true },
  { key: 'capturedOn', label: 'Captured On', visible: true },
  { key: 'candles', label: 'Chart', visible: true },
];

const BackendScreenerTable: React.FC<BackendScreenerTableProps> = ({
  columns = defaultColumns,
  viewChartButtonEnable = false,
  pageSize = 20,
  data,
  loading,
  error,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  screenerCode,
}) => {
  const isAIScreener = checkIsAI(screenerCode);
  const [chartView, setChartView]         = useState<string>('CLASSIC');
  const [analysisStock, setAnalysisStock] = useState<ScreenerStockResponse | null>(null);
  const [drawings, setDrawings]           = useState<Drawing[]>([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);

  // Fetch screener info to check chartView
  useEffect(() => {
    if (!screenerCode) return;
    screenerApi.getScreenerByCode(screenerCode)
      .then(res => setChartView(res.data.chartView ?? 'CLASSIC'))
      .catch(() => {});
  }, [screenerCode]);

  // Load drawings when a stock is selected for analysis
  useEffect(() => {
    if (!analysisStock || !screenerCode) return;
    setLoadingDrawings(true);
    screenerApi.getStockDrawings(screenerCode, analysisStock.id)
      .then(res => setDrawings(Array.isArray(res.data.drawings) ? res.data.drawings as Drawing[] : []))
      .catch(() => setDrawings([]))
      .finally(() => setLoadingDrawings(false));
  }, [analysisStock?.id, screenerCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewChart = (stock: ScreenerStockResponse) => {
    if (stock.chartUrl) {
      window.open(stock.chartUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const visibleColumns = columns.filter((col) => col.visible);

  const renderCellContent = (row: ScreenerStockResponse, columnKey: keyof ScreenerStockResponse) => {
    const value = row[columnKey];

    switch (columnKey) {
      case 'capturedPrice':
      case 'currentPrice':
      case 'maxUpsidePrice':
        return value !== null && value !== undefined ? `₹${Number(value).toFixed(2)}` : '-';

      case 'maxUpside':
      case 'currentUpside':
        if (value !== null && value !== undefined) {
          const numValue = Number(value);
          return (
            <span className={`max-move ${numValue >= 0 ? 'profit' : 'loss'}`}>
              {numValue >= 0 ? '+' : ''}{numValue.toFixed(2)}%
            </span>
          );
        }
        return '-';

      case 'volume':
      case 'marketCap':
        if (value !== null && value !== undefined) {
          const numValue = Number(value);
          if (numValue >= 10000000) {
            return `${(numValue / 10000000).toFixed(2)} Cr`;
          } else if (numValue >= 100000) {
            return `${(numValue / 100000).toFixed(2)} L`;
          }
          return numValue.toLocaleString();
        }
        return '-';

      case 'candles':
        if (showAnalysisButton && screenerCode) {
          return (
            <MiniChartWithDrawings
              candles={row.candles}
              stockId={row.id}
              screenerCode={screenerCode}
              height={125}
            />
          );
        }
        return <MiniChart candles={row.candles} width={180} height={60} />;

      default:
        return value !== null && value !== undefined ? String(value) : '-';
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const showAnalysisButton = !!screenerCode && chartView === 'DRAWING';

  return (
    <div className="screener-table-wrapper">
      {/* View Analysis modal */}
      {analysisStock && (
        <StockChartWithDrawings
          stock={analysisStock}
          drawings={loadingDrawings ? [] : drawings}
          onClose={() => { setAnalysisStock(null); setDrawings([]); }}
          isAIScreener={isAIScreener}
        />
      )}

      {/* Desktop Table View */}
      <div className="screener-table-container-desktop">
        {loading ? (
          <div className="screener-table-loading">
            <div className="screener-loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : error ? (
          <div className="screener-error">
            <p>Error: {error}</p>
          </div>
        ) : (
          <table className="screener-table">
            <thead>
              <tr>
                <th>#</th>
                {visibleColumns.map((col) => (
                  <th key={String(col.key)}>
                    {col.label}
                    {col.key === 'maxUpside' && (
                      <span
                        className="info-icon"
                        title="Maximum upside move after the captured price. Tracking continues until the closing price falls below the 45-day moving average."
                      >
                        &#9432;
                      </span>
                    )}
                  </th>
                ))}
                {(viewChartButtonEnable || showAnalysisButton) && <th></th>}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + ((viewChartButtonEnable || showAnalysisButton) ? 1 : 0) + 1} className="no-data">
                    No data available
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr key={row.id}>
                    <td className="sr-no">{(currentPage - 1) * pageSize + index + 1}</td>
                    {visibleColumns.map((col) => (
                      <td key={`${row.id}-${String(col.key)}`}>
                        {renderCellContent(row, col.key)}
                      </td>
                    ))}
                    {(viewChartButtonEnable || showAnalysisButton) && (
                      <td style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {viewChartButtonEnable && row.chartUrl && (
                          <button
                            className="view-chart-btn"
                            onClick={() => handleViewChart(row)}
                          >
                            View Chart
                          </button>
                        )}
                        {showAnalysisButton && (
                          <button
                            className="view-analysis-btn"
                            onClick={() => setAnalysisStock(row)}
                          >
                            View Analysis
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="screener-table-container-mobile">
        {loading ? (
          <div className="screener-table-loading">
            <div className="screener-loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : error ? (
          <div className="screener-error">
            <p>Error: {error}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="no-data">No data available</div>
        ) : (
          data.map((row, index) => (
            <div key={row.id} className="screener-mobile-card">
              <div className="screener-mobile-row">
                <div className="screener-mobile-label">#</div>
                <div className="screener-mobile-value sr-no">{(currentPage - 1) * pageSize + index + 1}</div>
              </div>
              {visibleColumns.map((col) => (
                <div key={`${row.id}-${String(col.key)}`} className="screener-mobile-row">
                  <div className="screener-mobile-label">{col.label}</div>
                  <div className="screener-mobile-value">
                    {renderCellContent(row, col.key)}
                  </div>
                </div>
              ))}
              {(viewChartButtonEnable && row.chartUrl) || showAnalysisButton ? (
                <div className="screener-mobile-action">
                  {viewChartButtonEnable && row.chartUrl && (
                    <button className="view-chart-btn" onClick={() => handleViewChart(row)}>
                      View Chart
                    </button>
                  )}
                  {showAnalysisButton && (
                    <button className="view-analysis-btn" onClick={() => setAnalysisStock(row)}>
                      View Analysis
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && !error && data.length > 0 && (
        <div className="screener-pagination">
          <div className="pagination-info">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
          </div>

          <div className="pagination-controls">
            <div className="page-navigation">
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="page-numbers">
                {getPageNumbers().map((page, index) =>
                  typeof page === 'number' ? (
                    <button
                      key={index}
                      className={`page-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ) : (
                    <span key={index} className="page-ellipsis">
                      {page}
                    </span>
                  )
                )}
              </div>

              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackendScreenerTable;
